import { BroadLocation } from '@parkupp/core'

interface MetroCentroid {
    lat: number
    lng: number
}

const METRO_CENTROIDS: Record<string, MetroCentroid> = {
    [BroadLocation.CAPE_TOWN]: { lat: -33.9249, lng: 18.4241 },
    [BroadLocation.JOHANNESBURG]: { lat: -26.2041, lng: 28.0473 },
    [BroadLocation.PRETORIA]: { lat: -25.7479, lng: 28.2293 },
    [BroadLocation.DURBAN]: { lat: -29.8587, lng: 31.0218 },
    [BroadLocation.GQEBERHA]: { lat: -33.9608, lng: 25.6022 },
    [BroadLocation.BLOEMFONTEIN]: { lat: -29.0852, lng: 26.1596 },
    [BroadLocation.EAST_LONDON]: { lat: -33.0153, lng: 27.9116 },
    [BroadLocation.NELSPRUIT]: { lat: -25.4753, lng: 30.9694 },
    [BroadLocation.KIMBERLEY]: { lat: -28.7282, lng: 24.7499 },
    [BroadLocation.POLOKWANE]: { lat: -23.9045, lng: 29.4689 },
    [BroadLocation.RUSTENBURG]: { lat: -25.6715, lng: 27.2420 },
}

const ALIAS_MAP: Record<string, string> = buildAliasMap()

function buildAliasMap(): Record<string, string> {
    const map: Record<string, string> = {}

    const aliases: Record<string, string[]> = {
        [BroadLocation.CAPE_TOWN]: [
            'cape town', 'kaapstad', 'cape town city centre', 'city of cape town',
            'cape town cbd', 'cpt', 'gardens', 'sea point', 'green point',
            'woodstock', 'observatory', 'claremont', 'rondebosch', 'newlands',
            'mowbray', 'salt river', 'century city', 'bellville', 'parow',
            'tyger valley', 'durbanville', 'brackenfell', 'stellenbosch',
            'paarl', 'somerset west', 'strand', 'milnerton', 'table view',
            'bloubergstrand', 'camps bay', 'hout bay', 'constantia',
            'kenilworth', 'wynberg', 'muizenberg', 'fish hoek',
            'simon\'s town', 'kalk bay', 'tokai', 'plumstead',
            'waterfront', 'v&a waterfront', 'foreshore', 'city bowl',
            'athlone', 'pinelands', 'thornton', 'goodwood', 'monte vista',
            'edgemead', 'bothasig', 'parklands', 'sunningdale',
        ],
        [BroadLocation.JOHANNESBURG]: [
            'johannesburg', 'joburg', 'jozi', 'jhb', 'johannesburg cbd',
            'sandton', 'rosebank', 'braamfontein', 'melrose arch',
            'fourways', 'midrand', 'randburg', 'roodepoort', 'florida',
            'soweto', 'bedfordview', 'edenvale', 'germiston', 'kempton park',
            'benoni', 'boksburg', 'alberton', 'greenstone', 'sunninghill',
            'morningside', 'rivonia', 'lonehill', 'bryanston', 'hyde park',
            'parkhurst', 'parktown', 'melville', 'auckland park',
            'norwood', 'illovo', 'houghton', 'killarney', 'orange grove',
            'greenside', 'emmarentia', 'craighall', 'dunkeld',
            'sandown', 'wynberg', 'kramerville', 'woodmead', 'dainfern',
            'city of johannesburg', 'johannesburg south', 'jnb',
        ],
        [BroadLocation.PRETORIA]: [
            'pretoria', 'tshwane', 'centurion', 'hatfield', 'menlyn',
            'brooklyn', 'arcadia', 'sunnyside', 'waterkloof', 'lynnwood',
            'irene', 'faerie glen', 'garsfontein', 'moreleta park',
            'montana', 'silverton', 'mamelodi', 'gezina', 'capital park',
            'wonderboom', 'sinoville', 'lyttelton', 'eldoraigne',
            'wierdapark', 'the reeds', 'hennopspark', 'rooihuiskraal',
            'city of tshwane', 'pta',
        ],
        [BroadLocation.DURBAN]: [
            'durban', 'ethekwini', 'durban cbd', 'umhlanga', 'ballito',
            'la lucia', 'morningside', 'berea', 'musgrave', 'glenwood',
            'overport', 'westville', 'pinetown', 'hillcrest',
            'kloof', 'amanzimtoti', 'umdloti', 'tongaat',
            'durban north', 'durban south', 'point', 'greyville',
            'city of durban', 'dbn', 'gateway',
        ],
        [BroadLocation.GQEBERHA]: [
            'gqeberha', 'port elizabeth', 'pe', 'nelson mandela bay',
            'summerstrand', 'walmer', 'newton park', 'south end',
            'central', 'humewood', 'mill park', 'richmond hill',
            'uitenhage', 'despatch',
        ],
        [BroadLocation.BLOEMFONTEIN]: [
            'bloemfontein', 'mangaung', 'bloem', 'universitas',
            'westdene', 'langenhovenpark', 'bayswater',
        ],
        [BroadLocation.EAST_LONDON]: [
            'east london', 'buffalo city', 'el', 'beacon bay',
            'gonubie', 'nahoon', 'vincent', 'cambridge',
        ],
        [BroadLocation.NELSPRUIT]: [
            'nelspruit', 'mbombela', 'white river', 'riverside park',
        ],
        [BroadLocation.KIMBERLEY]: [
            'kimberley', 'sol plaatje',
        ],
        [BroadLocation.POLOKWANE]: [
            'polokwane', 'pietersburg',
        ],
        [BroadLocation.RUSTENBURG]: [
            'rustenburg', 'phokeng',
        ],
    }

    for (const [metro, names] of Object.entries(aliases)) {
        for (const name of names) {
            map[name] = metro
        }
    }

    return map
}

const MAX_SNAP_DISTANCE_KM = 60

function haversineKm(lat1: number, lng1: number, lat2: number, lng2: number): number {
    const R = 6371
    const dLat = ((lat2 - lat1) * Math.PI) / 180
    const dLng = ((lng2 - lng1) * Math.PI) / 180
    const a = Math.sin(dLat / 2) ** 2 + Math.cos((lat1 * Math.PI) / 180) * Math.cos((lat2 * Math.PI) / 180) * Math.sin(dLng / 2) ** 2
    return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))
}

function snapToNearestMetro(lat: number, lng: number): string | null {
    let bestMetro: string | null = null
    let bestDist = Infinity

    for (const [metro, centroid] of Object.entries(METRO_CENTROIDS)) {
        const dist = haversineKm(lat, lng, centroid.lat, centroid.lng)
        if (dist < bestDist) {
            bestDist = dist
            bestMetro = metro
        }
    }

    return bestDist <= MAX_SNAP_DISTANCE_KM ? bestMetro : null
}

export interface NormalisationResult {
    metro: string
    resolved: boolean
}

export function normaliseCity(cityRaw: string | undefined, lat?: number, lng?: number): NormalisationResult {
    if (cityRaw) {
        const key = cityRaw.toLowerCase().trim()
        const aliasMatch = ALIAS_MAP[key]
        if (aliasMatch) {
            return { metro: aliasMatch, resolved: true }
        }
    }

    if (lat != null && lng != null && isFinite(lat) && isFinite(lng)) {
        const geoMatch = snapToNearestMetro(lat, lng)
        if (geoMatch) {
            return { metro: geoMatch, resolved: true }
        }
    }

    return { metro: BroadLocation.OTHER, resolved: false }
}

export function normaliseCityForSearch(cityRaw: string | undefined, latStr?: string, lngStr?: string): NormalisationResult {
    const lat = latStr ? parseFloat(latStr) : undefined
    const lng = lngStr ? parseFloat(lngStr) : undefined
    return normaliseCity(cityRaw, lat, lng)
}

export function getCanonicalMetros(): string[] {
    return Object.values(BroadLocation).filter((v) => v !== BroadLocation.OTHER)
}
